import { FormSelect, Tooltip, cents, centsToUnits } from '@systemeio/ui'
import { useAffiliateDashboardPayments } from 'modules/affiliate-dashboard/hooks/use-affiliate-dashboard-payments'
import {
  AffiliateDashboardFilterInterface,
  AffiliateDashboardFilterWithTagInterface,
  TagId,
} from 'modules/affiliate-dashboard/types/affiliate-dashboard-interface'
import { AffiliateDashboardPaymentsInterface } from 'modules/affiliate-dashboard/types/affiliate-dashboard-payments-interface'
import { AffiliateCommissionTypeEnum } from 'modules/affiliate-program/affiliate/enums/affiliate-commission-type-enum'
import React, { useEffect, useState } from 'react'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import TransLoco from 'shared/components/trans-loco'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import CommissionNextGenIcon from 'shared/icons/commission-next-gen-icon'
import CommissionSecondTierIcon from 'shared/icons/commission-second-tier-icon'
import ExclamationIcon from 'shared/icons/exclamation-icon'
import NotificationIcon from 'shared/icons/notification-icon'
import RefundIcon from 'shared/icons/refund-icon'
import TimeIcon from 'shared/icons/time-icon'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { SelectorDataInterface } from 'shared/types/selector-data-interface'
import { currencyFormat } from 'shared/utils/currency-format'
import { dateFormat, dateTimeFormat } from 'shared/utils/date-time-format'

interface AffiliateDashboardPaymentsProps {
  mainFilter: AffiliateDashboardFilterInterface
  defaultFilter: AffiliateDashboardFilterInterface
  tagsData: SelectorDataInterface<TagId>[] | undefined
  setMainFilter: React.Dispatch<React.SetStateAction<AffiliateDashboardFilterInterface>>
}

const AffiliateDashboardPayments = ({
  mainFilter,
  tagsData,
  defaultFilter,
  setMainFilter,
}: AffiliateDashboardPaymentsProps) => {
  const { t } = useLocoTranslation()
  const { limit, setLimit, pagination, setPagination, clearPagination } = usePagination(true)
  const { user } = useUser()

  const [filterWithTag, setFilterWithTag] = useState<AffiliateDashboardFilterWithTagInterface>({
    ...defaultFilter,
    affiliateTag: undefined,
  })

  useEffect(() => {
    setFilterWithTag(prev => ({ ...prev, ...mainFilter }))
  }, [mainFilter])

  const { affiliatePayments, counter } = useAffiliateDashboardPayments({
    filter: filterWithTag,
    pagination,
    limit,
  })

  const changeTag = (tagId: TagId) => {
    setFilterWithTag(prev => ({ ...prev, affiliateTag: tagId }))
    clearPagination()
  }

  useEffect(() => {
    clearPagination()
  }, [mainFilter, clearPagination])

  const columns: ColumnDefinitionInterface<AffiliateDashboardPaymentsInterface>[] = [
    {
      key: 'paymentItem',
      widthPercent: 55,
      header: t('dashboard.affiliate_dashboard.vendor_product'),
      onRender: payment => (
        <div className={'flex justify-between gap-3 w-full items-center truncate'}>
          <div className={'flex gap-3 items-center truncate w-full'}>
            <button
              onClick={() =>
                setMainFilter(prev => ({ ...prev, vendorId: payment.payment.user.id }))
              }
              className={
                'shrink-0 w-[32px] h-[32px] outline-none focus-visible:ring-2 ring-blue ring-offset-2 rounded-full'
              }
            >
              <img
                alt={payment.payment.user.affiliateProgramBrandName}
                className={'rounded-full w-[32px] h-[32px]'}
                src={payment.payment.user.avatar}
              />
            </button>
            <div
              className={`flex flex-col w-full truncate ${
                payment.isFirstPayment && 'font-bold text-darkblue'
              }`}
            >
              <div className={`flex gap-1 w-full truncate`}>
                {payment.payment.user.affiliateProgramBrandName && (
                  <>
                    <span className={'truncate max-w-1/2'}>
                      {payment.payment.user.affiliateProgramBrandName}
                    </span>
                    {payment.orderItem.offerPricePlan ? '/' : ''}
                  </>
                )}
                {payment.orderItem.offerPricePlan && (
                  <span className={'truncate'}>{payment.orderItem.offerPricePlan.innerName}</span>
                )}
              </div>
              <span className={'truncate'}>{payment.payment.customer.email}</span>
            </div>
          </div>
          {payment.isFirstPayment && (
            <Tooltip mode={'hover'} label={t('dashboard.affiliate.first_payment')}>
              <NotificationIcon />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'paymentItem',
      widthPercent: 30,
      header: t('dashboard.affiliate.commission'),
      onRender: payment => (
        <div className={'flex gap-5 w-full truncate items-center'}>
          {payment.affiliateCommission.type === AffiliateCommissionTypeEnum.first_tier &&
          payment.estimatedPayoutDate ? (
            <Tooltip
              mode={'hover'}
              wrapperClassName={'truncate'}
              label={
                <div className={'text-start'}>
                  <TransLoco
                    t={t}
                    i18nKey={'dashboard.affiliate.commission_tooltip'}
                    values={{
                      delay: payment.affiliateCommission.offer.payoutDelay,
                      date: dateFormat(payment.estimatedPayoutDate, {
                        dateFormat: user?.dateFormat,
                        timeZone: user?.timezone,
                      }),
                    }}
                  />
                </div>
              }
            >
              <span className={`truncate ${payment.isFirstPayment && 'font-bold text-darkblue'}`}>
                {currencyFormat(
                  centsToUnits(
                    cents(payment.amount * (payment.affiliateCommission.affiliateCommission / 100)),
                  ),
                  payment.payment.currency,
                  user?.dashboardLocale,
                )}
              </span>
            </Tooltip>
          ) : (
            <span className={`${payment.isFirstPayment && 'font-bold text-darkblue'}`}>
              {currencyFormat(
                centsToUnits(
                  cents(payment.amount * (payment.affiliateCommission.affiliateCommission / 100)),
                ),
                payment.payment.currency,
                user?.dashboardLocale,
              )}
            </span>
          )}
          <div className={'flex gap-1.5 items-center'}>
            {payment.payment.refundedAt && (
              <Tooltip
                mode={'hover'}
                label={`${t('global.refunded')} (${dateTimeFormat(payment.payment.refundedAt, {
                  timeFormat: user?.timeFormat,
                  dateFormat: user?.dateFormat,
                  timeZone: user?.timezone,
                })})`}
              >
                <RefundIcon className={'fill-orange w-[20px] h-[20px]'} />
              </Tooltip>
            )}
            {payment.affiliateCommission.cancelledAt && (
              <Tooltip mode={'hover'} label={t('dashboard.affiliate.commission_revoked')}>
                <ExclamationIcon className={'fill-danger w-[20px] h-[20px]'} />
              </Tooltip>
            )}
            {payment.payment.pending && (
              <Tooltip mode={'hover'} label={t('dashboard.affiliate.pending')}>
                <TimeIcon className={'fill-orange w-[20px] h-[20px]'} />
              </Tooltip>
            )}
            {payment.affiliateCommission.type === AffiliateCommissionTypeEnum.next_gen && (
              <Tooltip
                popoverClassName={'max-w-[250px] lg:max-w-[450px]'}
                mode={'hover'}
                label={t('dashboard.affiliate.next_gen_commission')}
              >
                <CommissionNextGenIcon />
              </Tooltip>
            )}
            {payment.affiliateCommission.type === AffiliateCommissionTypeEnum.second_tier && (
              <Tooltip mode={'hover'} label={t('dashboard.affiliate.second_tier_commission')}>
                <CommissionSecondTierIcon />
              </Tooltip>
            )}
          </div>
          {payment.payment.customer.affiliateTag && (
            <button
              onClick={() =>
                payment.payment.customer.affiliateTag &&
                changeTag(payment.payment.customer.affiliateTag.id)
              }
              className={`truncate outline-none focus-visible:ring-2 ring-blue ring-offset-2 h-fit rounded-lg bg-lightblue text-darkblue py-1 px-2 w-fit`}
            >
              {payment.payment.customer.affiliateTag.name}
            </button>
          )}
        </div>
      ),
    },
    {
      key: 'paymentItem',
      widthPercent: 15,
      header: t('global.date'),
      onRender: payment => (
        <Tooltip
          wrapperClassName={'w-full'}
          mode={'hover'}
          label={dateTimeFormat(payment.payment.paidAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        >
          <span
            className={`truncate w-full ${payment.isFirstPayment && 'font-bold text-darkblue'}`}
          >
            {dateFormat(payment.payment.paidAt, {
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone,
            })}
          </span>
        </Tooltip>
      ),
    },
  ]
  return (
    <ListingLayout
      data={affiliatePayments}
      title={t('dashboard.affiliate.payments')}
      type={'compact'}
      counter={counter}
      customHeaderElement={
        <FormSelect
          placeholder={t('dashboard.affiliate_dashboard.any_tag')}
          value={filterWithTag.affiliateTag}
          data={tagsData}
          onChange={data => setFilterWithTag(prev => ({ ...prev, affiliateTag: data }))}
        />
      }
    >
      <Table
        data={affiliatePayments}
        columns={columns}
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
      />
    </ListingLayout>
  )
}

export default AffiliateDashboardPayments
